// src/pages/finance-portal/fixed-assets/AssetForm.js
//
// Create / edit a fixed asset

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel,
    Grid, MenuItem, Paper, Switch, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK,
    DEPRECIATION_METHODS, DEPRECIATION_CONVENTIONS,
} from './components/fixedAssetFormatters';

const SOURCE_TYPES = [
    { value: 'DIRECT_PURCHASE', label: 'Direct Purchase' },
    { value: 'PO_RECEIPT', label: 'PO Receipt' },
    { value: 'CIP_CAPITALIZATION', label: 'CIP Capitalization' },
    { value: 'LEASE', label: 'Lease' },
    { value: 'OPENING_BALANCE', label: 'Opening Balance (Migration)' },
    { value: 'DONATION', label: 'Donation' },
    { value: 'INTERNAL_TRANSFER', label: 'Internal Transfer' },
];

const empty = {
    name: '', description: '', tag_number: '', serial_number: '', barcode: '',
    category_id: '', vendor_id: '',
    source_type: 'DIRECT_PURCHASE',
    cost_basis: '', residual_value: '', salvage_value: '',
    additional_capitalized_costs: '',
    acquisition_date: new Date().toISOString().slice(0, 10),
    in_service_date: '',
    useful_life_months: 60,
    book_method: 'STRAIGHT_LINE',
    book_convention: 'FULL_MONTH',
    declining_balance_rate: '',
    tax_method: 'MACRS_GDS',
    tax_recovery_period_years: '',
    section_179_deduction: '',
    bonus_depreciation_pct: '',
    location: '', department: '',
    insurance_value: '', replacement_cost: '',
    asset_account_id: '',
    accumulated_depreciation_account_id: '',
    depreciation_expense_account_id: '',
    notes: '',
    is_intangible: false,
    is_indefinite_life: false,
};

export default function AssetForm() {
    const { assetId } = useParams();
    const isEdit = Boolean(assetId);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [aiSuggesting, setAiSuggesting] = useState(false);

    const setField = (k, v) => {
        setForm(prev => ({ ...prev, [k]: v }));
        if (errors[k]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[k];
                return next;
            });
        }
    };

    const isPositiveNumber = (v) => v !== '' && v !== null && !Number.isNaN(Number(v)) && Number(v) > 0;
    const isNonNegativeNumber = (v) => v === '' || v === null || (!Number.isNaN(Number(v)) && Number(v) >= 0);
    const isInteger = (v) => /^\d+$/.test(String(v).trim());

    const validateForm = () => {
        const e = {};
        const cost = parseFloat(form.cost_basis);
        const residual = parseFloat(form.residual_value || 0);
        const lifeMonths = parseInt(form.useful_life_months, 10);
        const s179 = parseFloat(form.section_179_deduction || 0);
        const bonus = parseFloat(form.bonus_depreciation_pct || 0);

        if (!form.name || !form.name.trim()) {
            e.name = 'Asset name is required';
        } else if (form.name.length > 255) {
            e.name = 'Name must be 255 characters or fewer';
        }

        if (form.cost_basis === '' || form.cost_basis === null || form.cost_basis === undefined) {
            e.cost_basis = 'Cost basis is required';
        } else if (Number.isNaN(cost) || cost < 0) {
            e.cost_basis = 'Cost basis must be a non-negative number';
        }

        if (!form.acquisition_date) {
            e.acquisition_date = 'Acquisition date is required';
        }

        if (form.in_service_date && form.acquisition_date
            && form.in_service_date < form.acquisition_date) {
            e.in_service_date = 'In-service date cannot be before acquisition date';
        }

        if (!form.is_indefinite_life) {
            if (!form.useful_life_months && form.useful_life_months !== 0) {
                e.useful_life_months = 'Useful life is required';
            } else if (!isInteger(form.useful_life_months) || lifeMonths <= 0) {
                e.useful_life_months = 'Useful life must be a positive whole number of months';
            }
        }

        if (form.residual_value !== '' && form.residual_value !== null) {
            if (Number.isNaN(residual) || residual < 0) {
                e.residual_value = 'Residual value must be a non-negative number';
            } else if (!Number.isNaN(cost) && residual > cost) {
                e.residual_value = 'Residual value cannot exceed cost basis';
            }
        }

        if (!isNonNegativeNumber(form.salvage_value)) {
            e.salvage_value = 'Salvage value must be a non-negative number';
        }
        if (!isNonNegativeNumber(form.additional_capitalized_costs)) {
            e.additional_capitalized_costs = 'Additional capitalized costs must be non-negative';
        }

        if (form.section_179_deduction !== '' && form.section_179_deduction !== null) {
            if (Number.isNaN(s179) || s179 < 0) {
                e.section_179_deduction = 'Section 179 must be a non-negative number';
            } else if (!Number.isNaN(cost) && s179 > cost) {
                e.section_179_deduction = 'Section 179 cannot exceed cost basis';
            }
        }

        if (form.bonus_depreciation_pct !== '' && form.bonus_depreciation_pct !== null) {
            if (Number.isNaN(bonus) || bonus < 0 || bonus > 100) {
                e.bonus_depreciation_pct = 'Bonus depreciation must be between 0 and 100';
            }
        }

        if (form.tax_recovery_period_years && (!isInteger(form.tax_recovery_period_years)
            || parseInt(form.tax_recovery_period_years, 10) <= 0)) {
            e.tax_recovery_period_years = 'Recovery period must be a positive whole number of years';
        }

        if (form.declining_balance_rate && !isPositiveNumber(form.declining_balance_rate)) {
            e.declining_balance_rate = 'DB rate must be a positive number (e.g. 1.5 or 2.0)';
        }

        if (!isNonNegativeNumber(form.insurance_value)) {
            e.insurance_value = 'Insurance value must be non-negative';
        }
        if (!isNonNegativeNumber(form.replacement_cost)) {
            e.replacement_cost = 'Replacement cost must be non-negative';
        }

        return e;
    };

    const loadAsset = useCallback(async () => {
        if (!isEdit) return;
        try {
            const res = await axiosInstance.get(`/api/finance/fixed-assets/assets/${assetId}`);
            if (res.data?.success) {
                const a = res.data.data;
                setForm({
                    ...empty,
                    ...a,
                    category_id: a.category_id || '',
                    vendor_id: a.vendor_id || '',
                    asset_account_id: a.asset_account_id || '',
                    accumulated_depreciation_account_id: a.accumulated_depreciation_account_id || '',
                    depreciation_expense_account_id: a.depreciation_expense_account_id || '',
                    declining_balance_rate: a.declining_balance_rate || '',
                    tax_recovery_period_years: a.tax_recovery_period_years || '',
                    in_service_date: a.in_service_date || '',
                    description: a.description || '',
                    notes: a.notes || '',
                });
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId, isEdit]);

    useEffect(() => {
        loadAsset();
        axiosInstance.get('/api/finance/fixed-assets/categories')
            .then(r => { if (r.data?.success) setCategories(r.data.data || []); })
            .catch(() => {});
        axiosInstance.get('/api/finance/ap/vendors', { params: { per_page: 200 } })
            .then(r => {
                const list = r.data?.data || r.data?.vendors || [];
                setVendors(list);
            })
            .catch(() => {});
        axiosInstance.get('/api/finance/chart-of-accounts', { params: { per_page: 500 } })
            .then(r => {
                const list = r.data?.data || r.data?.accounts || [];
                setAccounts(list);
            })
            .catch(() => {});
    }, [loadAsset]);

    const handleSave = async () => {
        const fieldErrors = validateForm();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            enqueueSnackbar(`Fix ${Object.keys(fieldErrors).length} validation error(s) before saving`,
                { variant: 'warning' });
            return;
        }
        setErrors({});
        setSaving(true);
        try {
            const payload = {
                ...form,
                cost_basis: parseFloat(form.cost_basis) || 0,
                residual_value: parseFloat(form.residual_value) || 0,
                salvage_value: parseFloat(form.salvage_value) || 0,
                additional_capitalized_costs: parseFloat(form.additional_capitalized_costs) || 0,
                section_179_deduction: parseFloat(form.section_179_deduction) || 0,
                bonus_depreciation_pct: parseFloat(form.bonus_depreciation_pct) || 0,
                useful_life_months: parseInt(form.useful_life_months, 10) || 0,
                tax_recovery_period_years: form.tax_recovery_period_years
                    ? parseInt(form.tax_recovery_period_years, 10) : null,
                declining_balance_rate: form.declining_balance_rate
                    ? parseFloat(form.declining_balance_rate) : null,
                insurance_value: form.insurance_value ? parseFloat(form.insurance_value) : null,
                replacement_cost: form.replacement_cost ? parseFloat(form.replacement_cost) : null,
                in_service_date: form.in_service_date || null,
            };
            const res = isEdit
                ? await axiosInstance.put(`/api/finance/fixed-assets/assets/${assetId}`, payload)
                : await axiosInstance.post('/api/finance/fixed-assets/assets', payload);
            if (res.data?.success) {
                enqueueSnackbar(isEdit ? 'Asset updated' : 'Asset created', { variant: 'success' });
                navigate('/finance-portal/fixed-assets/assets');
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Save failed', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAiSuggest = async () => {
        if (!form.description || !form.cost_basis) {
            enqueueSnackbar('Add description and cost first', { variant: 'warning' });
            return;
        }
        setAiSuggesting(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/agent/recommend-capitalization', {
                description: form.description || form.name,
                cost: parseFloat(form.cost_basis) || 0,
            });
            if (res.data?.success) {
                const r = res.data.data?.recommendation;
                if (r) {
                    if (r.useful_life_months) setField('useful_life_months', r.useful_life_months);
                    if (r.depreciation_method) setField('book_method', r.depreciation_method);
                    enqueueSnackbar(
                        `AI: ${r.decision}${r.asset_class ? ` as ${r.asset_class}` : ''} — life ${r.useful_life_months || '?'}mo`,
                        { variant: 'info' });
                } else {
                    enqueueSnackbar('AI returned no structured suggestion', { variant: 'info' });
                }
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'AI suggestion failed', { variant: 'error' });
        } finally {
            setAiSuggesting(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: FA_ACCENT }} /></Box>;
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance-portal/fixed-assets/assets')}
                            sx={{ color: 'white', textTransform: 'none', fontFamily: FA_FONT }}>
                            Back
                        </Button>
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.5rem' }}>
                            {isEdit ? 'Edit Asset' : 'New Asset'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleAiSuggest} disabled={aiSuggesting} startIcon={<AutoAwesomeIcon />}
                            sx={{
                                fontFamily: FA_FONT, color: 'white', textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                            }}>
                            {aiSuggesting ? 'Asking AI...' : 'AI Suggest'}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                            variant="contained"
                            sx={{ fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: FA_ACCENT, '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}>
                            Save
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily: FA_FONT }}>{error}</Alert>}
                {Object.keys(errors).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, fontFamily: FA_FONT, borderRadius: '12px' }}>
                        Please fix {Object.keys(errors).length} validation error{Object.keys(errors).length === 1 ? '' : 's'} below before saving.
                    </Alert>
                )}

                {/* Identity */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                            Identity
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField fullWidth size="small" label="Asset Name *" required
                                    value={form.name} onChange={e => setField('name', e.target.value)}
                                    error={Boolean(errors.name)} helperText={errors.name || ' '}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" label="Tag Number"
                                    value={form.tag_number} onChange={e => setField('tag_number', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" multiline minRows={2} label="Description"
                                    value={form.description} onChange={e => setField('description', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" label="Serial Number"
                                    value={form.serial_number} onChange={e => setField('serial_number', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" label="Barcode"
                                    value={form.barcode} onChange={e => setField('barcode', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" select label="Category"
                                    value={form.category_id} onChange={e => setField('category_id', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    <MenuItem value="" sx={{ fontFamily: FA_FONT }}>None</MenuItem>
                                    {categories.map(c => (
                                        <MenuItem key={c.id} value={c.id} sx={{ fontFamily: FA_FONT }}>{c.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Location"
                                    value={form.location} onChange={e => setField('location', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Department"
                                    value={form.department} onChange={e => setField('department', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Acquisition */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                            Acquisition & Cost
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" select label="Source Type"
                                    value={form.source_type} onChange={e => setField('source_type', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    {SOURCE_TYPES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily: FA_FONT }}>{s.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" select label="Vendor"
                                    value={form.vendor_id} onChange={e => setField('vendor_id', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    <MenuItem value="" sx={{ fontFamily: FA_FONT }}>None</MenuItem>
                                    {vendors.map(v => (
                                        <MenuItem key={v.id} value={v.id} sx={{ fontFamily: FA_FONT }}>{v.display_name || v.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <TextField fullWidth size="small" type="date" label="Acquisition Date *" required
                                    value={form.acquisition_date} onChange={e => setField('acquisition_date', e.target.value)}
                                    error={Boolean(errors.acquisition_date)} helperText={errors.acquisition_date || ' '}
                                    InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }} />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <TextField fullWidth size="small" type="date" label="In Service Date"
                                    value={form.in_service_date} onChange={e => setField('in_service_date', e.target.value)}
                                    error={Boolean(errors.in_service_date)} helperText={errors.in_service_date || ' '}
                                    InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" type="number" label="Cost Basis *" required
                                    value={form.cost_basis} onChange={e => setField('cost_basis', e.target.value)}
                                    error={Boolean(errors.cost_basis)} helperText={errors.cost_basis || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" type="number" label="Residual Value"
                                    value={form.residual_value} onChange={e => setField('residual_value', e.target.value)}
                                    error={Boolean(errors.residual_value)} helperText={errors.residual_value || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth size="small" type="number" label="Additional Capitalized Costs"
                                    value={form.additional_capitalized_costs}
                                    onChange={e => setField('additional_capitalized_costs', e.target.value)}
                                    error={Boolean(errors.additional_capitalized_costs)}
                                    helperText={errors.additional_capitalized_costs || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Depreciation */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                            Depreciation Policy
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    label={<Typography sx={{ fontFamily: FA_FONT, fontSize: '0.9rem' }}>Intangible asset</Typography>}
                                    control={<Switch checked={!!form.is_intangible}
                                        onChange={e => setField('is_intangible', e.target.checked)} />}
                                />
                                <FormControlLabel
                                    label={<Typography sx={{ fontFamily: FA_FONT, fontSize: '0.9rem' }}>Indefinite life (no depreciation)</Typography>}
                                    control={<Switch checked={!!form.is_indefinite_life}
                                        onChange={e => setField('is_indefinite_life', e.target.checked)} />}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" type="number" label="Useful Life (months)"
                                    value={form.useful_life_months} onChange={e => setField('useful_life_months', e.target.value)}
                                    error={Boolean(errors.useful_life_months)} helperText={errors.useful_life_months || ' '}
                                    inputProps={{ min: 0, step: 1 }}
                                    disabled={!!form.is_indefinite_life}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" select label="Book Method"
                                    value={form.book_method} onChange={e => setField('book_method', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    {DEPRECIATION_METHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily: FA_FONT }}>{m.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" select label="Convention"
                                    value={form.book_convention} onChange={e => setField('book_convention', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    {DEPRECIATION_CONVENTIONS.map(c => <MenuItem key={c.value} value={c.value} sx={{ fontFamily: FA_FONT }}>{c.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" type="number" label="DB Rate (factor)"
                                    placeholder="1.5 / 2.0"
                                    value={form.declining_balance_rate}
                                    onChange={e => setField('declining_balance_rate', e.target.value)}
                                    error={Boolean(errors.declining_balance_rate)}
                                    helperText={errors.declining_balance_rate || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                        </Grid>

                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem', color: '#64748b', mt: 3, mb: 1 }}>
                            Tax depreciation (parallel)
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" select label="Tax Method"
                                    value={form.tax_method} onChange={e => setField('tax_method', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                    {DEPRECIATION_METHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily: FA_FONT }}>{m.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" type="number" label="Recovery Period (yrs)"
                                    value={form.tax_recovery_period_years}
                                    onChange={e => setField('tax_recovery_period_years', e.target.value)}
                                    error={Boolean(errors.tax_recovery_period_years)}
                                    helperText={errors.tax_recovery_period_years || ' '}
                                    inputProps={{ min: 0, step: 1 }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" type="number" label="Section 179 Deduction"
                                    value={form.section_179_deduction}
                                    onChange={e => setField('section_179_deduction', e.target.value)}
                                    error={Boolean(errors.section_179_deduction)}
                                    helperText={errors.section_179_deduction || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth size="small" type="number" label="Bonus Depreciation %"
                                    value={form.bonus_depreciation_pct}
                                    onChange={e => setField('bonus_depreciation_pct', e.target.value)}
                                    error={Boolean(errors.bonus_depreciation_pct)}
                                    helperText={errors.bonus_depreciation_pct || ' '}
                                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* GL accounts */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                            GL Accounts (override category defaults)
                        </Typography>
                        <Grid container spacing={2}>
                            {[
                                ['asset_account_id', 'Asset Account'],
                                ['accumulated_depreciation_account_id', 'Accumulated Depreciation'],
                                ['depreciation_expense_account_id', 'Depreciation Expense'],
                            ].map(([key, label]) => (
                                <Grid item xs={12} md={4} key={key}>
                                    <TextField fullWidth size="small" select label={label}
                                        value={form[key]} onChange={e => setField(key, e.target.value)}
                                        InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                        <MenuItem value="" sx={{ fontFamily: FA_FONT }}>From Category</MenuItem>
                                        {accounts.map(acc => (
                                            <MenuItem key={acc.id} value={acc.id} sx={{ fontFamily: FA_FONT }}>
                                                {acc.account_number} — {acc.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* Insurance & misc */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                            Insurance & Notes
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField fullWidth size="small" type="number" label="Insurance Value"
                                    value={form.insurance_value} onChange={e => setField('insurance_value', e.target.value)}
                                    error={Boolean(errors.insurance_value)} helperText={errors.insurance_value || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth size="small" type="number" label="Replacement Cost"
                                    value={form.replacement_cost} onChange={e => setField('replacement_cost', e.target.value)}
                                    error={Boolean(errors.replacement_cost)} helperText={errors.replacement_cost || ' '}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" multiline minRows={2} label="Notes"
                                    value={form.notes} onChange={e => setField('notes', e.target.value)}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
